import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import {
  ActivityLibraryFilters,
  fetchActivityLibrary,
  saveActivityToLibrary,
  promoteActivityToAgency,
  demoteActivityFromAgency,
  addLibraryActivityToItinerary,
  deleteActivityFromLibrary,
} from '@/services/library.service';
import { LibraryActivityPayload } from '@/lib/library/models/library-activity.model';

export const ACTIVITY_LIBRARY_QUERY_KEY = 'activity-library';

const activityLibraryQueryKey = (filters: ActivityLibraryFilters = {}) => [
  ACTIVITY_LIBRARY_QUERY_KEY,
  filters.activityType ?? null,
  filters.tags ?? null,
  filters.search ?? null,
  filters.scope ?? null,
];

/**
 * Query TanStack per la lista della libreria personalizzata di attività,
 * filtrabile per tipo attività, tag, ricerca testuale e ambito (personale/agenzia).
 * `options.enabled` permette di rimandare la fetch (es. finché l'utente non ha scelto
 * l'ambito nella libreria del membro agenzia).
 */
export const useActivityLibraryQuery = (
  filters: ActivityLibraryFilters = {},
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: activityLibraryQueryKey(filters),
    queryFn: () => fetchActivityLibrary(filters),
    enabled: options.enabled ?? true,
  });
};

/**
 * Mutation per salvare (insert/update) un'attività nella libreria.
 * `agencyId` valorizzato salva nella libreria di quell'agenzia, null come personale.
 * Invalida la query lista e mostra un toast di esito.
 */
export const useSaveActivityToLibrary = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({
      activity,
      agencyId,
    }: {
      activity: LibraryActivityPayload;
      agencyId: string | null;
    }) => saveActivityToLibrary(activity, agencyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_LIBRARY_QUERY_KEY] });
      toast({ variant: 'success',
        title: t('library.saveSuccessTitle'),
        description: t('library.saveSuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error saving activity to library:', error);
      toast({
        title: t('common.error'),
        description: t('library.saveErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Mutation per promuovere un'attività personale nella libreria dell'agenzia
 * (setta agency_id lato server). La riga resta comunque in libreria personale.
 * Invalida la query lista e mostra un toast di esito.
 */
export const usePromoteActivityToAgency = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => promoteActivityToAgency(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_LIBRARY_QUERY_KEY] });
      toast({ variant: 'success',
        title: t('library.promoteSuccessTitle'),
        description: t('library.promoteSuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error promoting activity to agency library:', error);
      toast({
        title: t('common.error'),
        description: t('library.promoteErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Mutation per rimuovere una propria attività dalla libreria dell'agenzia
 * (setta agency_id = null lato server), rendendola di nuovo solo personale.
 * Inverso di `usePromoteActivityToAgency`. Invalida la query lista e mostra un toast.
 */
export const useDemoteActivityFromAgency = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => demoteActivityFromAgency(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_LIBRARY_QUERY_KEY] });
      toast({ variant: 'success',
        title: t('library.demoteSuccessTitle'),
        description: t('library.demoteSuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error demoting activity from agency library:', error);
      toast({
        title: t('common.error'),
        description: t('library.demoteErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Mutation per eliminare una entry dalla libreria personale/di agenzia.
 * Invalida la query lista e mostra un toast di esito. La RLS DELETE lato server
 * resta l'unica fonte di verità sui permessi: questa mutation non fa altro
 * controllo di ownership, si limita a propagare l'eventuale errore.
 */
export const useDeleteActivityFromLibrary = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => deleteActivityFromLibrary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_LIBRARY_QUERY_KEY] });
      toast({ variant: 'success',
        title: t('library.deleteSuccessTitle'),
        description: t('library.deleteSuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error deleting activity from library:', error);
      toast({
        title: t('common.error'),
        description: t('library.deleteErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Mutation per copiare un'attività di libreria in un itinerario, in una data specifica.
 * Mostra un toast di esito. L'invalidation della lista attività dell'itinerario è a
 * carico degli hook dedicati del builder (fuori dallo scope di questo modulo dati).
 */
export const useAddLibraryActivityToItinerary = () => {
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({
      libraryActivityId,
      itineraryId,
      date,
      dayIndex,
    }: {
      libraryActivityId: string;
      itineraryId: string;
      /** Data reale, oppure null su un template (che usa `dayIndex`). */
      date: string | null;
      dayIndex?: number | null;
    }) => addLibraryActivityToItinerary(libraryActivityId, itineraryId, date, dayIndex),
    onSuccess: () => {
      toast({ variant: 'success',
        title: t('library.addToItinerarySuccessTitle'),
        description: t('library.addToItinerarySuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error adding library activity to itinerary:', error);
      toast({
        title: t('common.error'),
        description: t('library.addToItineraryErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};
